import { makeAutoObservable } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { Amount } from 'src_common/common/amount/Amount';
import { DateTime } from 'src_common/utils/time/time';
import { z } from 'zod';
import { PaymentMethodDropdownState } from 'src/domains/players/webview/components/WithdrawAndDeposit';

export const optionType = z.object({
    label: z.string(),
    value: z.string(),
    expires: z.string().optional().nullable(),
    monthExpires: z.string().optional().nullable(),
    isDisabled: z.boolean(),
    netDeposit: z.string().optional().nullable(),
    isSeparator: z.boolean(),
    provider: z.string(),
});

export type OptionType = z.infer<typeof optionType>;

export class NewPaymentMethodDropdownState {
    private languagesState: LanguagesState;

    public constructor(
        private allowAddingMethod: boolean,
        private common: Common,
        private paymentMeythodDropdownState: PaymentMethodDropdownState
    ) {
        this.languagesState = LanguagesState.get(common);
        makeAutoObservable(this);
    }

    public onChange = (selectedOption: unknown): void => {
        const parsedOptionType = optionType.safeParse(selectedOption);
        if (parsedOptionType.success) {
            this.paymentMeythodDropdownState.handlePaymentSelectChange(parsedOptionType.data.value);
            return;
        }
        console.error('Payment Methods Dropdown - payment method onChange error');
        return;
    };

    public get allOptions(): OptionType[] {
        if (this.paymentMeythodDropdownState.savedPaymentMethodsWithNetDeposits === null) {
            return [];
        }
        let finalOptions: OptionType[] = [];

        const optionsParsed: OptionType[] = this.paymentMeythodDropdownState.savedPaymentMethodsWithNetDeposits.map(
            (v) => {
                const cardNumber: string = v.number === undefined ? '0000' : v.number;
                return {
                    value: v.id,
                    label: `**** **** **** ${cardNumber.slice(-4)}`,
                    expires: DateTime.from(v.expires ?? undefined)?.format('DD-MM-YYYY') ?? null,
                    monthExpires: DateTime.from(v.expires ?? undefined)?.format('MM/YY') ?? null,
                    isDisabled: DateTime.current().isAfter(DateTime.from(v.expires ?? undefined) ?? DateTime.current()),
                    netDeposit:
                        v.netDeposit === null || v.netDeposit === undefined
                            ? null
                            : BasicDataModel.get(this.common).money(new Amount(v.netDeposit), false),
                    isSeparator: false,
                    provider: v.provider,
                };
            }
        );

        if (this.allowAddingMethod) {
            optionsParsed.unshift({
                label: this.languagesState.getTranslation(
                    'account.payment-method-dropdown.new.label',
                    'New payment method'
                ),
                value: 'none',
                expires: null,
                monthExpires: null,
                isDisabled: false,
                netDeposit: null,
                isSeparator: false,
                provider: 'none',
            });
            return optionsParsed;
        }

        const nonExpiredCards = optionsParsed.filter((card) => !card.isDisabled);
        const expiredCards = optionsParsed.filter((card) => card.isDisabled);
        finalOptions = finalOptions.concat(nonExpiredCards);
        if (expiredCards.length > 0 && nonExpiredCards.length > 0)
            finalOptions.push({
                value: '',
                label: '',
                expires: null,
                monthExpires: null,
                isDisabled: false,
                netDeposit: null,
                isSeparator: true,
                provider: 'none',
            });
        finalOptions = finalOptions.concat(expiredCards);

        return finalOptions;
    }

    public get activeElement(): OptionType | null {
        const active =
            this.allOptions.find((option) => this.paymentMeythodDropdownState.paymentMethodsValue === option.value) ??
            null;
        return active;
    }
}
